import type { AuthConfig } from '../types/types'
import type { AccessTokenContactData, Tokens } from '../types/tokenTypes'
import logHandler from '../handlers/logHandler'

export const authApiHandler = (config: AuthConfig) => {
  const log = logHandler(config.debug ?? false)

  let inflightRefresh: Promise<Tokens | null> | null = null

  const getAccessToken = async (
    idToken: string,
    impersonatedContactId?: number
  ): Promise<string | null> => {
    log.info(`Called get access token with contactId: ${impersonatedContactId}`)

    return await fetch(`${getAuthHost()}/getaccesstoken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        impersonatedContactId,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`code: ${response.status} with message: ${response.statusText}`)
        }
        return response.json()
      })
      .then(data => data.micrositeToken)
      .catch((e: Error) => {
        log.error(`Error trying to get access token: ${e}`)
        return null
      })
  }

  const getZendeskToken = async (
    idToken: string,
    impersonatedContactId?: number
  ): Promise<string | null> => {
    log.info(`Called get zendesk token with contactId: ${impersonatedContactId}`)

    return await fetch(`${getAuthHost()}/getzendesktoken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        impersonatedContactId,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`code: ${response.status} with message: ${response.statusText}`)
        }
        return response.json()
      })
      .then(data => data.zendeskToken)
      .catch((e: Error) => {
        log.error(`Error trying to get zendesk token: ${e}`)
        return null
      })
  }

  const getTokens = async (
    code: string,
    provider: string,
    impersonatedContactId?: number
  ): Promise<Tokens | null> => {
    log.info(`Called get tokens with contactId: ${impersonatedContactId}, provider: ${provider}`)

    return await fetch(`${getAuthHost()}/${provider}/gettokens`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        impersonatedContactId,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`code: ${response.status} with message: ${response.statusText}`)
        }
        return response.json()
      })
      .catch((e: Error) => {
        log.error(`Error trying to get token: ${e}`)
        return null
      })
  }

  const refreshTokens = async (
    refreshToken: string,
    provider: string,
    contactId?: number
  ): Promise<Tokens | null> => {
    log.info(`Called get refresh tokens with contactId: ${contactId}, provider: ${provider}`)

    // Deduplicate concurrent refresh calls. If a refresh is already in-flight,
    // return the same promise so only one HTTP request reaches auth-api/Okta.
    // This prevents the race condition where Okta rotates the token on the first
    // call, causing subsequent concurrent calls with the now-invalid token to fail.
    if (inflightRefresh) {
      log.info('Refresh already in-flight, awaiting existing request')
      return inflightRefresh
    }

    inflightRefresh = fetch(`${getAuthHost()}/${provider}/refreshtokens`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refreshToken,
        contactId,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`code: ${response.status} with message: ${response.statusText}`)
        }
        return response.json()
      })
      .then((tokens: Tokens) => {
        inflightRefresh = null
        return tokens
      })
      .catch(() => {
        inflightRefresh = null
        return null
      })

    return inflightRefresh
  }

  const getAuthHost = () => {
    const host =
      typeof window !== 'undefined' && window.location.hostname !== 'localhost'
        ? `${window.location.origin}/auth`
        : config.authServerUrl

    return host
  }

  const getContacts = async (
    idToken: string,
    impersonatedContactId?: number
  ): Promise<AccessTokenContactData[]> => {
    return await fetch(`${getAuthHost()}/getcontacts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        impersonatedContactId,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`code: ${response.status} with message: ${response.statusText}`)
        }
        return response.json()
      })
      .then(data => data.contacts)
      .catch((e: Error) => {
        log.error(`Error trying to get contacts: ${e}`)
        return []
      })
  }

  return {
    getAccessToken,
    getTokens,
    refreshTokens,
    getContacts,
    getZendeskToken,
  }
}
